import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ClipboardList } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { getActivityLabel } from "@/lib/taskActivities";

interface TasksPreviewProps {
  northId: string;
}

interface ListingTask {
  id: string;
  title: string;
  notes: string | null;
  activity_date: string;
  activity_type: string;
}

export const TasksPreview = ({ northId }: TasksPreviewProps) => {
  const [showAll, setShowAll] = useState(false);

  const { data: tasks } = useQuery({
    queryKey: ["tasks-preview", northId],
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("listing_tasks" as any)
        .select("*")
        .eq("north_id", northId)
        .order("activity_date", { ascending: false }) as any);

      if (error) throw error;
      return (data || []) as unknown as ListingTask[];
    },
  });

  if (!tasks || tasks.length === 0) {
    return null;
  }

  const displayedTasks = showAll ? tasks : tasks.slice(0, 5);
  const hasMore = tasks.length > 5;

  return (
    <div>
      <h3 className="text-sm font-semibold tracking-wider text-foreground mb-6">
        WHAT WE'VE DONE FOR YOUR HOME
      </h3>

      <div className="space-y-4">
        {displayedTasks.map((task) => (
          <div
            key={task.id}
            className="bg-background border border-border rounded-lg p-6"
          >
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <ClipboardList className="h-6 w-6 text-foreground" />
                </div>
              </div>

              <div className="flex-1">
                <div className="text-xs text-muted-foreground mb-1">
                  {format(new Date(task.activity_date), "M/d/yyyy")}
                </div>
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {task.title}
                </h4>
                {task.notes && (
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {task.notes}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}

        {hasMore && !showAll && (
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => setShowAll(true)}
              className="w-full sm:w-auto"
            >
              Load More Tasks ({tasks.length - 5} more)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
